import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Crown, Zap, Shield } from 'lucide-react';

export default function Subscription() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchTenant(), fetchPlans()]).then(() => setLoading(false));
  }, []);

  const fetchTenant = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenant/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTenant(data);
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
      toast.error('Bilgiler yüklenirken hata oluştu');
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/plans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Paketler yüklenirken hata oluştu');
    }
  };

  const handleUpgrade = (planId) => {
    navigate('/checkout', { state: { planId } });
  };

  const getCurrentPlan = () => {
    if (!tenant || !tenant.subscription.plan_id) return null;
    
    // First check if plan_details is provided by backend
    if (tenant.plan_details) {
      return tenant.plan_details;
    }
    
    // Fallback to finding in plans list
    return plans.find(p => p.id === tenant.subscription.plan_id);
  };

  const getSubscriptionStatusBadge = () => {
    if (!tenant) return null;
    
    const status = tenant.subscription.status;
    const badges = {
      trial: { color: 'bg-blue-100 text-blue-800', text: 'Deneme' },
      active: { color: 'bg-green-100 text-green-800', text: 'Aktif' },
      expired: { color: 'bg-red-100 text-red-800', text: 'Süresi Dolmuş' },
      suspended: { color: 'bg-yellow-100 text-yellow-800', text: 'Askıya Alınmış' }
    };
    
    const badge = badges[status] || badges.trial;
    
    return (
      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const currentPlan = getCurrentPlan();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Abonelik</h1>
          <p className="mt-1 text-sm text-gray-500">
            Abonelik planınızı yönetin ve yükseltin
          </p>
        </div>
      </div>

      {/* Current Subscription */}
      {tenant && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Mevcut Aboneliğiniz</h2>
              {getSubscriptionStatusBadge()}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Plan</p>
                <p className="text-lg font-semibold text-gray-900">
                  {currentPlan ? currentPlan.name : 'Deneme'}
                </p>
              </div>
              
              <div className="border-r border-gray-200 pr-6">
                <p className="text-sm text-gray-500 mb-1">Kalan Soru Hakkı</p>
                <p className="text-lg font-semibold text-gray-900">
                  {tenant.subscription.status === 'trial'
                    ? `${tenant.subscription.trial_questions_left} (Deneme)`
                    : tenant.subscription.bonus_questions > 0
                    ? `${tenant.subscription.bonus_questions} (Bonus 🎁)`
                    : currentPlan && currentPlan.features
                    ? `${currentPlan.features.questions_per_month - (tenant.subscription.questions_used || 0)} / ${currentPlan.features.questions_per_month}`
                    : `Sınırsız`
                  }
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500 mb-1">Bu Ay Kullanılan</p>
                <p className="text-lg font-semibold text-gray-900">
                  {tenant.subscription.questions_used || 0}
                </p>
              </div>
            </div>

            {tenant.subscription.status === 'trial' && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  💡 <strong>Deneme sürümündesiniz.</strong> {tenant.subscription.trial_questions_left} soru hakkınız kaldı.
                  Daha fazla soru için bir plan seçin.
                </p>
              </div>
            )}

            {tenant.subscription.bonus_questions > 0 && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-sm text-amber-800">
                  🎁 <strong>Bonus soru hakkınız var!</strong> {tenant.subscription.bonus_questions} adet hediye soru kullanabilirsiniz.
                </p>
              </div>
            )}

            {tenant.subscription.status === 'active' && currentPlan && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800 mb-2">
                  ✅ <strong>{currentPlan.name} Planı Aktif</strong>
                </p>
                <div className="text-xs text-green-700 space-y-1">
                  <p>• Aylık {currentPlan.features.questions_per_month} soru hakkı</p>
                  <p>• AI Model: {currentPlan.features.ai_model}</p>
                  <p>• Destek: {currentPlan.features.support}</p>
                  {tenant.subscription.next_billing_date && (
                    <p>• Yenilenme Tarihi: {new Date(tenant.subscription.next_billing_date).toLocaleDateString('tr-TR')}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Available Plans */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Paketler</h2>
            
            {plans.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Henüz paket bulunmamaktadır.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  const isCurrentPlan = currentPlan && currentPlan.id === plan.id;
                  
                  return (
                    <div
                      key={plan.id}
                      className={`bg-white rounded-lg shadow-md overflow-hidden ${
                        isCurrentPlan ? 'ring-2 ring-orange-500' : ''
                      }`}
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                          {isCurrentPlan && (
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                              Mevcut Plan
                            </span>
                          )}
                        </div>
                        
                        <div className="mb-6">
                          <div className="flex items-baseline">
                            <span className="text-4xl font-bold text-gray-900">
                              {plan.price}
                            </span>
                            <span className="ml-2 text-gray-500">₺ / {plan.period === 'monthly' ? 'ay' : 'yıl'}</span>
                          </div>
                        </div>

                        <ul className="space-y-3 mb-6">
                          <li className="flex items-start">
                            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600">
                              <strong>{plan.features.questions_per_month}</strong> soru/ay
                            </span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600">
                              AI Model: <strong>{plan.features.ai_model}</strong>
                            </span>
                          </li>
                          <li className="flex items-start">
                            <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-600">
                              {plan.features.support}
                            </span>
                          </li>
                          {plan.features.auto_mode && (
                            <li className="flex items-start">
                              <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">
                                Otomatik Yanıtlama
                              </span>
                            </li>
                          )}
                        </ul>

                        <button
                          onClick={() => handleUpgrade(plan.id)}
                          disabled={isCurrentPlan}
                          className={`w-full py-2 px-4 rounded-md font-medium ${
                            isCurrentPlan
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'bg-orange-600 text-white hover:bg-orange-700'
                          }`}
                        >
                          {isCurrentPlan ? 'Mevcut Planınız' : 'Bu Planı Seç'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
